import { Search } from 'lucide-react';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { Input } from '../../ui/Input';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/Select';
import type {
  BaseColumnConfig,
  ColumnFilterConfig,
  FilterState,
  FilterValue,
  StandaloneFilter,
} from './types';

const TEXT_DEBOUNCE_MS = 400;
// Radix Select forbids an empty-string item value, so the "All" row carries a
// sentinel that maps back to `undefined` (no filter).
const ALL_VALUE = '__all__';

type Filterable = { id: string; header: string; filter: ColumnFilterConfig };

// 'labeled' = external "<Label>" beside the control (default, admin grids).
// 'inline' = label folded into the trigger ("Type: All"), matching the DS
// dashboard/inbox filter bar.
export type GridFilterVariant = 'labeled' | 'inline';

type Props<TRow> = {
  columns: BaseColumnConfig<TRow>[];
  standaloneFilters?: StandaloneFilter[];
  filters: FilterState;
  onFilterChange: (columnId: string, value: FilterValue) => void;
  search?: {
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
  };
  // Rendered inline right after the filters (e.g. a "Clear all" link).
  inlineAction?: React.ReactNode;
  // Rendered pushed to the far right of the bar (e.g. a "Create" button).
  headerActions?: React.ReactNode;
  variant?: GridFilterVariant;
  testIdPrefix: string;
};

const FILTER_LABEL_CLASS = 'text-sm font-medium text-muted-foreground';

export function GridFilterBar<TRow>({
  columns,
  standaloneFilters = [],
  filters,
  onFilterChange,
  search,
  inlineAction,
  headerActions,
  variant = 'labeled',
  testIdPrefix,
}: Props<TRow>) {
  const filterable: Filterable[] = [
    ...columns
      .filter((c) => c.filter)
      .map((c) => ({ id: c.id, header: c.header, filter: c.filter! })),
    ...standaloneFilters,
  ];
  if (!search && filterable.length === 0 && !headerActions && !inlineAction) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
      {search && (
        <SearchInput
          placeholder={search.placeholder}
          value={search.value}
          onChange={search.onChange}
          variant={variant}
          testIdPrefix={testIdPrefix}
        />
      )}
      {filterable.map((col) => (
        <FilterControl
          key={col.id}
          field={col}
          value={filters[col.id]}
          onChange={(v) => onFilterChange(col.id, v)}
          variant={variant}
          testIdPrefix={testIdPrefix}
        />
      ))}
      {inlineAction}
      {headerActions && <div className="ml-auto">{headerActions}</div>}
    </div>
  );
}

type SearchInputProps = {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  variant: GridFilterVariant;
  testIdPrefix: string;
};

function SearchInput({ placeholder, value, onChange, variant, testIdPrefix }: SearchInputProps) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const t = setTimeout(() => {
      onChange(draft);
    }, TEXT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draft, value, onChange]);

  return (
    <div className={variant === 'inline' ? 'relative w-full sm:w-64' : 'relative w-full max-w-md'}>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        type="search"
        placeholder={placeholder ?? 'Search...'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-9 w-full pl-9"
        data-testid={`${testIdPrefix}-search`}
      />
    </div>
  );
}

type ControlProps = {
  field: Filterable;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  variant: GridFilterVariant;
  testIdPrefix: string;
};

function FilterControl({ field, value, onChange, variant, testIdPrefix }: ControlProps) {
  const filter = field.filter;
  const testId = `${testIdPrefix}-filter-${field.id}`;

  if (filter.type === 'text') {
    return (
      <TextFilter
        label={field.header}
        value={typeof value === 'string' ? value : ''}
        onChange={onChange}
        testId={testId}
      />
    );
  }

  // Entities that grow (clients, people, task types) are searched on the
  // server and paged as the list scrolls, rather than listed whole.
  if (filter.type === 'search') {
    const allLabel = filter.allLabel ?? 'All';
    const picker = (
      <SearchableSelect
        variant={variant === 'inline' ? 'inline' : 'field'}
        value={typeof value === 'string' && value !== '' ? value : ALL_VALUE}
        options={[{ value: ALL_VALUE, label: allLabel, pinned: true }, ...filter.options]}
        placeholder={allLabel}
        ariaLabel={field.header}
        selectedLabel={filter.selectedLabel}
        onSearch={filter.onSearch}
        loading={filter.loading}
        hasMore={filter.hasMore}
        onLoadMore={filter.onLoadMore}
        onChange={(v) => onChange(v === ALL_VALUE ? undefined : v)}
      />
    );
    if (variant === 'inline') return <div data-testid={testId}>{picker}</div>;
    return (
      <div className="flex items-center gap-2" data-testid={testId}>
        <span className={FILTER_LABEL_CLASS}>{field.header}</span>
        <div className="w-56">{picker}</div>
      </div>
    );
  }

  if (filter.type === 'select') {
    const current = typeof value === 'string' && value !== '' ? value : ALL_VALUE;
    return (
      <SelectFilter
        label={field.header}
        value={current}
        variant={variant}
        allLabel={filter.allLabel}
        testId={testId}
        onValueChange={(v) => onChange(v === ALL_VALUE ? undefined : v)}
      >
        {filter.options.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            data-testid={`${testId}-option-${opt.value}`}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectFilter>
    );
  }

  const triValue = value === true ? 'true' : value === false ? 'false' : ALL_VALUE;
  return (
    <SelectFilter
      label={field.header}
      value={triValue}
      variant={variant}
      testId={testId}
      onValueChange={(v) => onChange(v === ALL_VALUE ? undefined : v === 'true')}
    >
      <SelectItem value="true" data-testid={`${testId}-option-true`}>
        Yes
      </SelectItem>
      <SelectItem value="false" data-testid={`${testId}-option-false`}>
        No
      </SelectItem>
    </SelectFilter>
  );
}

type SelectFilterProps = {
  label: string;
  value: string;
  variant: GridFilterVariant;
  allLabel?: string;
  testId: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
};

function SelectFilter({
  label,
  value,
  variant,
  allLabel,
  testId,
  onValueChange,
  children,
}: SelectFilterProps) {
  // `w-fit` overrides SelectTrigger's baked-in `w-full` so the inline pill hugs
  // its content ("Type: All types") instead of stretching across the bar.
  const trigger = (
    <SelectTrigger
      className={variant === 'inline' ? 'h-9 w-fit gap-2' : 'h-9 w-40'}
      data-testid={testId}
      aria-label={label}
    >
      {variant === 'inline' && <span className={FILTER_LABEL_CLASS}>{label}:</span>}
      <SelectValue />
    </SelectTrigger>
  );

  const select = (
    <Select value={value} onValueChange={onValueChange}>
      {trigger}
      <SelectContent>
        <SelectItem value={ALL_VALUE} data-testid={`${testId}-option-all`}>
          {allLabel ?? 'All'}
        </SelectItem>
        {children}
      </SelectContent>
    </Select>
  );

  if (variant === 'inline') return select;

  return (
    <div className="flex items-center gap-2">
      <label className={FILTER_LABEL_CLASS}>{label}</label>
      {select}
    </div>
  );
}

type TextFilterProps = {
  label: string;
  value: string;
  onChange: (value: FilterValue) => void;
  testId: string;
};

function TextFilter({ label, value, onChange, testId }: TextFilterProps) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (draft === value) return;
    const t = setTimeout(() => {
      onChange(draft === '' ? undefined : draft);
    }, TEXT_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draft, value, onChange]);

  return (
    <div className="flex items-center gap-2">
      <label className={FILTER_LABEL_CLASS}>{label}</label>
      <Input
        type="search"
        placeholder="Search..."
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-9 w-64"
        data-testid={testId}
      />
    </div>
  );
}
